import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Sparkles, Camera, Layers, Award, Mail, ShoppingBag, Eye, Heart, Bookmark } from 'lucide-react';

const MOCKUP_IMAGES = {
  spotlightDiscovery: "https://static.prod-images.emergentagent.com/jobs/0eb6b925-9cc5-41a2-aca3-21b96797a8c8/images/d340e58512616792ca817d5d1fb55cd28010184c4adb45ceee550b00c892d408.png",
  createLookThemes: "https://static.prod-images.emergentagent.com/jobs/0eb6b925-9cc5-41a2-aca3-21b96797a8c8/images/c0d6f9e52ea29a54d915a250b4428a1970a4dbb80068e92b46b4fa864735c113.png",
  shopTheLook: "https://static.prod-images.emergentagent.com/jobs/0eb6b925-9cc5-41a2-aca3-21b96797a8c8/images/80b71b4049621a60548f7ceb4c76d947d17e8acdc7a95e88cc1c5cd1ebbb0e11.png",
  createPost: "https://static.prod-images.emergentagent.com/jobs/0eb6b925-9cc5-41a2-aca3-21b96797a8c8/images/c34aab69b984d25bb5e5835e46055dce1ab12f7b11cad51d3d4b51513e707000.png",
  createChecklist: "https://static.prod-images.emergentagent.com/jobs/0eb6b925-9cc5-41a2-aca3-21b96797a8c8/images/406ebe98a227b78684c9a9471851e093cf4a7570431bec22fe4f608e4b4fa888.png",
};

const themes = [
  { name: "Summer", styles: "Beach Vibes, Brunch Scene, Pool Party, Festival Ready, Golden Hour, Tropical Escape" },
  { name: "Wedding", styles: "Bridal Glam, Bridesmaid, Mehendi/Haldi, Sangeet Night, Reception, Guest Look" },
  { name: "Corporate", styles: "Office Everyday, Zoom Ready, Power Meeting, Business Casual, Conference Day" },
  { name: "Everyday", styles: "No-Makeup Makeup, School/College, Errand Run, Coffee Date, Work From Home" },
  { name: "Night Out", styles: "Club Night, Dinner Date, Girls Night, Cocktail Party, Bar Hopping" },
  { name: "Festival / Cultural", styles: "Diwali Glam, Eid Look, Christmas, Holi Ready, Pongal/Onam, Navratri" },
  { name: "Glam / Editorial", styles: "Red Carpet, Photoshoot, Runway Inspired, Bold & Artistic, Vintage Glam" },
  { name: "Seasonal", styles: "Monsoon Proof, Winter Cozy, Spring Fresh, Autumn Warm" },
  { name: "Skincare Journey", styles: "General" },
  { name: "Before & After", styles: "Side-by-side transformation photos" },
];

const steps = [
  {
    icon: CheckCircle,
    title: "1. Create a Checklist",
    desc: "A checklist is a curated list of 3-7 beauty products you use for a look or routine.",
    details: [
      "Go to the Checklists tab",
      "Tap \"Create Checklist\"",
      "Name it (e.g., \"My Everyday Skincare\")",
      "Choose a category: Face Care, Make Up, Hair Care, or Pharma",
      "Search and add 3\u20137 products from our 8,500+ database",
      "Save it"
    ],
    mockup: "createChecklist",
    mockupCaption: "Name your checklist, pick a category, then search and add products from the database.",
  },
  {
    icon: Camera,
    title: "2. Create a Post",
    desc: "Posts are like Instagram \u2014 photos with a caption \u2014 but with your product checklist attached.",
    details: [
      "Tap the \"+\" button",
      "Upload 1-5 photos (carousel supported)",
      "Write a caption",
      "Attach your checklist (required)",
      "Post it"
    ],
    mockup: "createPost",
    mockupCaption: "Upload photos, write your caption, and attach a product checklist before posting.",
  },
  {
    icon: Layers,
    title: "3. Create a Look on Spotlight",
    desc: "Looks appear in the Spotlight discovery feed. Anyone can find them \u2014 not just your followers.",
    details: [
      "Go to the Spotlight tab",
      "Tap \"+\" to create a Look",
      "Choose a theme and style (e.g., Summer > Beach Vibes)",
      "Upload photos and attach a checklist",
      "Post your Look"
    ],
    mockup: "createLookThemes",
    mockupCaption: "Pick a theme that matches your look \u2014 from Summer vibes to Wedding glam.",
  },
  {
    icon: Award,
    title: "4. Apply for Glow Badge",
    desc: "The Glow Badge is Gloverse\u2019s creator verification \u2014 like a blue tick for beauty.",
    details: [
      "Go to Settings > Glow Badge",
      "Tap \"Apply for Glow Badge\"",
      "Tell us about your content and niche",
      "We'll review and notify you"
    ],
    mockup: null,
  }
];

function MockupImage({ src, alt, caption }) {
  return (
    <div className="my-5">
      <div className="bg-gray-50 rounded-2xl p-3 sm:p-4 border border-gray-100">
        <img
          src={src}
          alt={alt}
          className="w-full max-w-[280px] mx-auto rounded-xl shadow-lg"
          loading="lazy"
        />
      </div>
      {caption && (
        <p className="text-xs text-gray-400 text-center mt-2 italic">{caption}</p>
      )}
    </div>
  );
}

export default function GuidePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" data-testid="guide-page">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-break { page-break-before: always; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      {/* Header */}
      <div className="no-print sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full" data-testid="guide-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => window.print()}
            className="px-4 py-2 bg-black text-white rounded-xl text-sm font-medium hover:bg-gray-800 transition-colors"
            data-testid="guide-save-pdf-btn"
          >
            Save as PDF
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10">
        {/* Hero */}
        <div className="text-center mb-12">
          <img src="/gloverse-logo.png" alt="Gloverse" className="w-48 mx-auto mb-4" style={{ filter: 'brightness(0.2)' }} />
          <h1 className="text-3xl font-black text-gray-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Influencer Onboarding Guide
          </h1>
          <p className="text-lg text-gray-500 max-w-lg mx-auto">
            Everything you need to get started on Gloverse &mdash; the beauty-first social platform.
          </p>
        </div>

        {/* What is Gloverse */}
        <section className="mb-12" data-testid="guide-section-what-is">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            What is Gloverse?
          </h2>
          <div className="bg-gray-50 rounded-2xl p-6 text-gray-700 leading-relaxed space-y-3">
            <p>
              Gloverse is a social media app built for the beauty community. Unlike Instagram or TikTok,
              Gloverse is purpose-built around <strong>product checklists</strong> &mdash; the real products behind every look.
            </p>
            <p>
              When you post on Gloverse, your followers don't just see a pretty photo. They see <strong>exactly</strong> what
              products you used &mdash; and they can shop them directly.
            </p>
          </div>
        </section>

        {/* Steps */}
        <section className="mb-12" data-testid="guide-section-getting-started">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Getting Started
          </h2>
          <div className="space-y-6">
            {steps.map((step) => (
              <div key={step.title} className="border border-gray-200 rounded-2xl p-6" data-testid={`guide-step-${step.title}`}>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-orange-400 flex items-center justify-center shrink-0">
                    <step.icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{step.title}</h3>
                    <p className="text-gray-600 text-sm mb-3">{step.desc}</p>
                    <ol className="space-y-1.5">
                      {step.details.map((d, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-gray-700">
                          <span className="w-5 h-5 bg-gray-100 text-gray-500 rounded-full flex items-center justify-center text-xs font-medium shrink-0 mt-0.5">{j + 1}</span>
                          {d}
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
                {step.mockup && MOCKUP_IMAGES[step.mockup] && (
                  <MockupImage
                    src={MOCKUP_IMAGES[step.mockup]}
                    alt={step.title}
                    caption={step.mockupCaption}
                  />
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Product Not Found - Highlighted Callout */}
        <section className="mb-12" data-testid="guide-section-missing-product">
          <div className="relative border-2 border-pink-400 rounded-2xl p-6 bg-gradient-to-br from-pink-50 via-white to-orange-50 shadow-sm">
            <div className="absolute -top-3.5 left-5 px-3 py-0.5 bg-pink-500 text-white text-xs font-bold rounded-full uppercase tracking-wide">
              Important
            </div>
            <h3 className="text-lg font-bold text-gray-900 mt-1 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Can't find a product? Here's how to add it.
            </h3>
            <p className="text-sm text-gray-700 mb-4">
              Our database has 8,500+ products, but if the product you use isn't available while creating a checklist, you can <strong>submit it yourself</strong>. Here's how:
            </p>
            <ol className="space-y-2 mb-4">
              <li className="flex items-start gap-2 text-sm text-gray-700">
                <span className="w-5 h-5 bg-pink-100 text-pink-600 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">1</span>
                <span>Go to <strong>Profile &rarr; Settings</strong> (gear icon on your profile page)</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-700">
                <span className="w-5 h-5 bg-pink-100 text-pink-600 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">2</span>
                <span>Scroll to <strong>"Suggest a Product"</strong> and tap <strong>"Add Product"</strong></span>
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-700">
                <span className="w-5 h-5 bg-pink-100 text-pink-600 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">3</span>
                <span>Fill in the product name, brand, category, type, price, and purchase link</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-700">
                <span className="w-5 h-5 bg-pink-100 text-pink-600 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">4</span>
                <span>Tap <strong>"Submit Product"</strong> &mdash; we'll verify and add it to the database</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-700">
                <span className="w-5 h-5 bg-pink-100 text-pink-600 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">5</span>
                <span>Once approved, the product will be available for <strong>everyone</strong> to use in their checklists</span>
              </li>
            </ol>
            <div className="bg-white/80 border border-pink-200 rounded-xl p-3">
              <p className="text-sm text-pink-800">
                <strong>Don't let a missing product stop you!</strong> Submit it once and it becomes available for the entire Gloverse community. The more products you add, the better the experience for everyone.
              </p>
            </div>
          </div>
        </section>

        {/* NEW: How Looks Work - Detailed Section */}
        <section className="mb-12 print-break" data-testid="guide-section-looks">
          <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            How "Looks" Work
          </h2>
          <p className="text-gray-500 text-sm mb-6">
            Looks are the heart of Gloverse. Here's how to discover, view, and shop them.
          </p>

          {/* Browsing Looks */}
          <div className="border border-gray-200 rounded-2xl p-6 mb-6" data-testid="guide-looks-browse">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shrink-0">
                <Eye className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Browsing Looks on Spotlight</h3>
                <p className="text-gray-600 text-sm">
                  Spotlight is Gloverse's discovery feed &mdash; like Instagram's Explore page, but organized by beauty themes.
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 space-y-3 text-sm text-gray-700">
              <div className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />
                <span><strong>Filter by theme</strong> &mdash; Tap theme pills like "Summer," "Wedding," or "Night Out" to browse specific look categories.</span>
              </div>
              <div className="flex items-start gap-2">
                <Heart className="w-4 h-4 text-pink-500 shrink-0 mt-0.5" />
                <span><strong>Like looks</strong> &mdash; Tap the heart icon to like a look. Likes help creators get discovered.</span>
              </div>
              <div className="flex items-start gap-2">
                <Bookmark className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
                <span><strong>Save for later</strong> &mdash; Tap the bookmark icon to save any look. Access saved looks anytime from the Spotlight tab.</span>
              </div>
            </div>
            <MockupImage
              src={MOCKUP_IMAGES.spotlightDiscovery}
              alt="Spotlight discovery feed showing themed beauty looks"
              caption="The Spotlight tab lets you browse beauty Looks filtered by themes like Summer, Wedding, and more."
            />
          </div>

          {/* Shopping a Look */}
          <div className="border border-gray-200 rounded-2xl p-6 mb-6" data-testid="guide-looks-shop">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shrink-0">
                <ShoppingBag className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Shopping a Look</h3>
                <p className="text-gray-600 text-sm">
                  Every Look on Gloverse has a product checklist attached. Followers can see and buy the exact products used.
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 space-y-3 text-sm text-gray-700">
              <p className="font-medium text-gray-900">How your followers shop your Look:</p>
              <ol className="space-y-2">
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">1</span>
                  <span>They tap on your Look from the Spotlight feed</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">2</span>
                  <span>The full Look opens with your photos and caption</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">3</span>
                  <span>They scroll down to see <strong>"Shop the Look"</strong> &mdash; your product checklist</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">4</span>
                  <span>Each product shows the name, brand, price, and a <strong>"Buy"</strong> link to purchase it directly</span>
                </li>
              </ol>
            </div>
            <MockupImage
              src={MOCKUP_IMAGES.shopTheLook}
              alt="Look detail view with Shop the Look product checklist"
              caption="When someone opens your Look, they see the 'Shop the Look' section with every product you used."
            />
            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-sm text-amber-800">
                <strong>Pro tip:</strong> The more specific your checklist, the more your followers trust your recommendations.
                Always include the exact shade, variant, or size when you add products.
              </p>
            </div>
          </div>

          {/* Saving Looks */}
          <div className="border border-gray-200 rounded-2xl p-6 mb-6" data-testid="guide-looks-save">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center shrink-0">
                <Bookmark className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Saving Looks for Later</h3>
                <p className="text-gray-600 text-sm">
                  Found a Look you love? Save it &mdash; and its entire product checklist comes with it.
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-pink-50 rounded-xl p-4 space-y-3 text-sm text-gray-700">
              <p className="font-medium text-gray-900">How saving works:</p>
              <ol className="space-y-2">
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">1</span>
                  <span>Tap the <strong>bookmark icon</strong> on any Look in the Spotlight feed or in the Look detail view</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">2</span>
                  <span>The Look &mdash; along with its <strong>full product checklist</strong> &mdash; is saved to your collection</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">3</span>
                  <span>Access your saved Looks anytime from the <strong>bookmark icon</strong> at the top of the Spotlight tab</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">4</span>
                  <span>Open any saved Look to see the photos, caption, and the complete <strong>"Shop the Look"</strong> checklist with buy links</span>
                </li>
              </ol>
            </div>
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-sm text-blue-800">
                <strong>For creators:</strong> When followers save your Look, they keep your entire product recommendation.
                This means they can come back to shop your products days or weeks later &mdash; more saves means more reach and more product clicks.
              </p>
            </div>
          </div>

          {/* Why Looks matter */}
          <div className="bg-gradient-to-br from-pink-50 to-orange-50 rounded-2xl p-6" data-testid="guide-looks-why">
            <h3 className="text-lg font-bold text-gray-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Why Looks matter for creators
            </h3>
            <div className="space-y-3 text-sm text-gray-700">
              <div className="flex items-start gap-3">
                <span className="font-bold text-pink-500 text-lg leading-none mt-0.5">1.</span>
                <span><strong>Discovery</strong> &mdash; Looks are the #1 way new users find you. When someone browses "Summer &mdash; Beach Vibes," your Look shows up even if they don't follow you.</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-bold text-pink-500 text-lg leading-none mt-0.5">2.</span>
                <span><strong>Shoppable content</strong> &mdash; Every Look includes a checklist. Your followers can shop your exact products with one tap.</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-bold text-pink-500 text-lg leading-none mt-0.5">3.</span>
                <span><strong>Theme organization</strong> &mdash; Unlike Instagram's algorithm, Gloverse organizes content by theme. Your "Bridal Glam" Look sits alongside other wedding content &mdash; reaching exactly the right audience.</span>
              </div>
            </div>
          </div>
        </section>

        {/* Themes Table */}
        <section className="mb-12 print-break" data-testid="guide-section-themes">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Available Themes & Styles
          </h2>
          <div className="border border-gray-200 rounded-2xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-5 py-3 text-sm font-semibold text-gray-700 border-b">Theme</th>
                  <th className="text-left px-5 py-3 text-sm font-semibold text-gray-700 border-b">Styles</th>
                </tr>
              </thead>
              <tbody>
                {themes.map((t) => (
                  <tr key={t.name} className="even:bg-white odd:bg-gray-50/50">
                    <td className="px-5 py-3 text-sm font-semibold text-gray-900 whitespace-nowrap">{t.name}</td>
                    <td className="px-5 py-3 text-sm text-gray-600">{t.styles}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Comparison */}
        <section className="mb-12" data-testid="guide-section-comparison">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Why Gloverse?
          </h2>
          <div className="border border-gray-200 rounded-2xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-5 py-3 text-sm font-semibold text-gray-700 border-b">Feature</th>
                  <th className="text-left px-5 py-3 text-sm font-semibold text-gray-700 border-b">Instagram</th>
                  <th className="text-left px-5 py-3 text-sm font-semibold text-gray-700 border-b">Gloverse</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Product tagging", "Limited, brand-sponsored", "Built-in, any product"],
                  ["Product checklists", "Not available", "Core feature"],
                  ["Themed discovery", "Algorithm-based", "Organized by Look themes"],
                  ["Shop products", "Via brand links", "Direct from every post"],
                  ["Verification", "Reserved for celebrities", "Open for creators"],
                ].map(([feature, ig, gl]) => (
                  <tr key={feature} className="even:bg-white odd:bg-gray-50/50">
                    <td className="px-5 py-3 text-sm font-medium text-gray-900">{feature}</td>
                    <td className="px-5 py-3 text-sm text-gray-500">{ig}</td>
                    <td className="px-5 py-3 text-sm text-gray-900 font-medium">{gl}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Launch Checklist */}
        <section className="mb-12" data-testid="guide-section-launch">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Your Launch Checklist
          </h2>
          <div className="bg-gradient-to-br from-pink-50 to-orange-50 rounded-2xl p-6 space-y-3">
            {[
              "Set up your profile (photo, username, bio)",
              "Create your first checklist (your signature routine)",
              "Post with your checklist attached",
              "Create at least one Look on Spotlight",
              "Share your Gloverse profile with your followers",
              "Tell your followers: \"I'm on Gloverse \u2014 follow me there to see exactly what products I use!\""
            ].map((item) => (
              <label key={item} className="flex items-start gap-3 cursor-pointer">
                <div className="w-5 h-5 border-2 border-gray-300 rounded mt-0.5 shrink-0" />
                <span className="text-sm text-gray-700">{item}</span>
              </label>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section className="text-center pb-10" data-testid="guide-section-contact">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 rounded-2xl">
            <Mail className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-700">Questions? Reach out at <strong>info@gloverse.in</strong></span>
          </div>
          <p className="text-lg font-bold text-gray-900 mt-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Welcome to the Glow community!
          </p>
          <p className="text-sm text-gray-500 mt-1">&mdash; Team Gloverse</p>
        </section>
      </div>
    </div>
  );
}
